// Unstyled-by-default banner that reads the login-time plan alert from
// context. Apps can either:
//   - drop <PlanAlertBanner /> in their AdminShell and style via className,
//   - or call usePlanAlert() and render their own with the design-system.

import React from 'react';
import { usePlanAlert } from '../hooks/usePlanAlert';
import { usePlanContext } from '../hooks/PlanProvider';
import type { PlanAlert } from '../types';

export interface PlanAlertBannerProps {
  className?: string;
  ctaLabel?: string;
  onNavigate?: (url: string) => void;
  render?: (alert: PlanAlert, ctaUrl: string) => React.ReactNode;
  /** Override the default short message per alert kind */
  messages?: Partial<Record<PlanAlert['kind'], string>>;
}

const DEFAULT_MESSAGES: Record<PlanAlert['kind'], string> = {
  EXPIRING_SOON: 'Your plan is about to expire.',
  LIMIT_NEAR: "You're approaching one of your plan limits.",
  // Caso 15 — stronger language than LIMIT_NEAR; this is the upgrade-now moment.
  LIMIT_CRITICAL:
    "You're about to run out — upgrade now to keep your event running.",
  EXPIRED: 'Your plan has expired — your account is now on Free.',
};

export function PlanAlertBanner({
  className,
  ctaLabel = 'View plans',
  onNavigate,
  render,
  messages,
}: PlanAlertBannerProps) {
  const alert = usePlanAlert();
  const { upgradeUrl } = usePlanContext();
  if (!alert) return null;

  const cta = alert.ctaUrl ?? upgradeUrl;
  if (render) return <>{render(alert, cta)}</>;

  const text = messages?.[alert.kind] ?? DEFAULT_MESSAGES[alert.kind];
  const handleClick = (e: React.MouseEvent) => {
    if (onNavigate) {
      e.preventDefault();
      onNavigate(cta);
    }
  };

  return (
    <div
      role="alert"
      className={className ?? 'plan-sdk-alert-banner'}
      data-plan-alert-kind={alert.kind}
    >
      <span>{text}</span>
      {alert.daysRemaining != null && alert.kind === 'EXPIRING_SOON' && (
        <span className="plan-sdk-alert-days">
          {' '}
          ({alert.daysRemaining} day{alert.daysRemaining === 1 ? '' : 's'} left)
        </span>
      )}
      <a href={cta} onClick={handleClick} className="plan-sdk-alert-cta">
        {ctaLabel}
      </a>
    </div>
  );
}
